import React, { FC, useEffect, useState, forwardRef, useImperativeHandle, useMemo } from "react";
import {
  Button,
  Form,
  message,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnGroupType, ColumnType, ColumnsType } from "antd/es/table";
import {
  GetDataOrderListData,
  GetDataOrderListItems,
  GetOrderDetailData,
} from "services/interface";
import { formatDate, formatDateWithTime } from "utils/dateFormat";
import {
  calculateTimeDifference,
  getPaymentName,
  getPaymentStatusName,
} from "utils/convertData";
import { isOrderSelectableForShippingLabel } from "./prepareShipSelection";
import ModalOderDetail from "../OrderTabsCard/ModalOrderDetail";
import { useAppDispatch, useAppSelector } from "../../../store";
import { setLoading } from "store/slices/loadingSlice";
import { useGetOrderDetail } from "hooks/useOrders";
import { setDataPrepareShipOffset, setDataPrepareShipLimit } from "store/slices/orderSlice";
import { apiGetShippingQueueStatus, checkShippingItems } from "services/ordersApi";
import { PrinterOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ModalUpdateOrder from "../OrderTabsCard/ModalUpdateOrder";
import { numberFormat } from "utils/nuberFormat";
import ModalRefundPayment from "../OrderTabsCard/ModalRefundPayment";
import moment from "moment";
import ShippingResultModal from "./ShippingResultModal";
import SelfTrackingModal from "./SelfTrackingModal";
import { ShipItemsSummary, getSelfTrackingState } from "./selfTracking";
import { RetailPickupCompleteCell } from "../RetailPickupCompleteCell";
import {
  SHIPPING_LABEL_STATUS_COLUMN_WIDTH,
  ShippingLabelStatusCell,
} from "../ShippingLabelStatusCell";

/** ปุ่มรับที่จุดจำหน่าย: enable เฉพาะ queueStatus === 'none' (หรือยังไม่โหลด) — นอกนั้น disabled */
function isRetailPickupDisabledByShippingQueue(
  queueStatus: string | undefined
): boolean {
  return Boolean(queueStatus && queueStatus !== "none");
}

interface OrderTableListProps {
  data?: GetDataOrderListData;
  IsHiddenPersonInfo?: boolean;
  refetchData: () => void;
  childrenBank: React.ReactNode[];
  refectShipped: () => void;
  refetchCancel?: () => void;
  hasSpeedMerchant: boolean;
  hasThailandPostMerchant: boolean;
  selectedRowKeys: React.Key[];
  onSelectChange: (newSelectedRowKeys: React.Key[]) => void;
  factory_id?: string;
}

export interface OrderTableListRef {
  overrideQueueStatus: (ids: string[], status: string) => void;
  getSelectableOrderIds: () => string[];
  /** ดึง queue-status + check_shipping_items ใหม่ (หลังออก Label) */
  refreshShippingState: () => void;
}

const OrderTableList = forwardRef<OrderTableListRef, OrderTableListProps>(({
  data,
  IsHiddenPersonInfo,
  refetchData,
  childrenBank,
  refectShipped,
  refetchCancel,
  hasSpeedMerchant,
  hasThailandPostMerchant,
  selectedRowKeys,
  onSelectChange,
  factory_id,
}, ref) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tabsKey = 1;
  const offset = useAppSelector((state) => state.order.dataPrepareShip.offset);
  const limit = useAppSelector((state) => state.order.dataPrepareShip.limit);
  const [showDetail, setShowDetail] = useState(false);
  const [modalData, setModalData] = useState<GetOrderDetailData>();
  const [currentPage, setCurrentPage] = useState(1);

  const [showEdit, setShowEdit] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [refId, setRefId] = useState<string>('')
  const [refType, setRefType] = useState<string>('')

  const [queueStatus, setQueueStatus] = useState<Record<string, string>>({});
  const [retryOrderId, setRetryOrderId] = useState<string | null>(null);
  /** order_id → รายการในออเดอร์ (วิธีส่ง + สถานะ) จาก checkShippingItems */
  const [shipItemsByOrderId, setShipItemsByOrderId] = useState<Record<string, ShipItemsSummary>>({});
  const [selfTrackingOrderId, setSelfTrackingOrderId] = useState<string | null>(null);

  const selectableIds = useMemo(() => {
    if (!data?.order_data) return [];
    return data.order_data
      .filter((record) => isOrderSelectableForShippingLabel(record, queueStatus, shipItemsByOrderId))
      .map((record) => record.order_id);
  }, [data?.order_data, queueStatus, shipItemsByOrderId]);

  useImperativeHandle(ref, () => ({
    overrideQueueStatus: (ids: string[], status: string) => {
      setQueueStatus(prev => {
        const next = { ...prev };
        ids.forEach(id => {
          next[id] = status;
        });
        return next;
      });
    },
    getSelectableOrderIds: () => selectableIds,
    // ส่งเองออก Label แล้ว queue-status ยังเป็น none — ต้องดึง has_label ใหม่ ไม่งั้น poll ทับ override แล้ว checkbox กลับมา
    refreshShippingState: () => {
      fetchShipItems();
      refreshQueueStatus();
    },
  }));

  const contactData = useAppSelector((state) => state.auth.contact);
  const use_platform_transport = contactData?.use_platform_transport;
  console.log("use_platform_transport", use_platform_transport);
  console.log("hasSpeedMerchant", hasSpeedMerchant);
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
    // ออก Label ครบแล้ว / เข้าคิวแล้ว ไม่ต้องออกซ้ำ
    getCheckboxProps: (record: GetDataOrderListItems) => ({
      disabled: !isOrderSelectableForShippingLabel(record, queueStatus, shipItemsByOrderId),
    }),
    renderCell: (checked: boolean, record: GetDataOrderListItems, index: number, originNode: React.ReactNode) => {
      const isDisabled = !isOrderSelectableForShippingLabel(record, queueStatus, shipItemsByOrderId);

      // ถ้าสถานะคือ Disabled ให้ return null เพื่อซ่อน Checkbox ออกไปจากหน้าจอ
      if (isDisabled) {
        return null;
      }
      return originNode;
    }
  };
  console.log("queueStatus", queueStatus)
  const showEditModal = (order_id: string, ref_id: string) => {
    setShowEdit(true);
  }

  const { mutate } = useGetOrderDetail();

  useEffect(() => {
    setCurrentPage(Math.floor(offset / limit) + 1);
  }, [offset, limit]);

  useEffect(() => {
    let interval: any;
    const fetchQueueStatus = async () => {
      if (!data?.order_data || data.order_data.length === 0) return;
      const orderIds = data.order_data.map(o => o.order_id);
      try {
        const res = await apiGetShippingQueueStatus(orderIds);
        if (res.status === 'success' && res.data) {
          setQueueStatus(res.data);
        }
      } catch (e) {
        console.error("Polling queue status error", e);
      }
    };

    if (data?.order_data && data.order_data.length > 0) {
      fetchQueueStatus();
      interval = setInterval(fetchQueueStatus, 30000); // 30s
    }

    return () => {
      if (interval) clearInterval(interval);
    }
  }, [data?.order_data]);

  // ดูว่าออเดอร์ไหนมีชิ้น "ส่งเอง" ที่ยังกรอกเลขพัสดุได้ก่อนเรียกขนส่ง
  const fetchShipItems = async () => {
    if (!data?.order_data || data.order_data.length === 0) {
      setShipItemsByOrderId({});
      return;
    }
    try {
      const res = await checkShippingItems(data.order_data.map((o) => o.order_id));
      if (res.status === "success" && res.data?.details) {
        const map: Record<string, ShipItemsSummary> = {};
        for (const d of res.data.details) {
          map[d.order_id] = { y: d.y ?? 0, z: d.z ?? 0, items: d.items };
        }
        setShipItemsByOrderId(map);
      }
    } catch (e) {
      console.error("checkShippingItems error (prepare ship tab)", e);
    }
  };

  useEffect(() => {
    fetchShipItems();
  }, [data?.order_data]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshQueueStatus = () => {
    const orderIds = data?.order_data.map(o => o.order_id) || [];
    if (orderIds.length > 0) {
      apiGetShippingQueueStatus(orderIds).then((res) => {
        if (res.status === 'success' && res.data) setQueueStatus(res.data);
      }).catch(e => console.error(e));
    }
  };

  const [currentTime, setCurrentTime] = useState(moment());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(moment());
    }, 60000);

    dispatch(setDataPrepareShipOffset(0));
    dispatch(setDataPrepareShipLimit(10));

    return () => clearInterval(interval);
  }, []);

  const [isProductProblem, setIsProductProblem] = useState<boolean>(false);
  const forceOpenDetail = (order_id: string, is_problem: boolean, payment_status: string, payment_amount: number, final_price: number) => {
    dispatch(setLoading(true));

    mutate(
      {
        key_type: "order_id",
        group_by: "factory",
        items: [order_id],
        factory_id: factory_id,
      },
      {
        onSuccess: async (data) => {
          setModalData({ ...data.data[0], payment_status: payment_status, payment_amount: payment_amount, final_price: final_price });

          setIsProductProblem(is_problem);

          await new Promise((resolve) => setTimeout(resolve, 200));

          dispatch(setLoading(false));

          setShowDetail(true);
        },
        onError: (error) => {
          console.log(error);
          dispatch(setLoading(false));
        },
      })
  }

  const columns: ColumnsType<GetDataOrderListItems> = [
    {
      title: "Order No.",
      dataIndex: "order_code",
      key: "order_id",
      render: (_text, record) => {
        let restPrice = -((record.total_order_price + record.transport_price) - (record.discount_amount + record.payment_amount));
        return (
          <Typography.Link
            style={{
              cursor: "pointer",
              fontWeight: "400",
              textDecoration: "underline",
            }}
            type="secondary"
            onClick={async () => {
              dispatch(setLoading(true));

              mutate(
                {
                  key_type: "order_id",
                  group_by: "factory",
                  items: [record.order_id],
                  factory_id: factory_id,
                },
                {
                  onSuccess: async (data) => {
                    setModalData({ ...data.data[0], payment_status: record.payment_status, payment_amount: record.payment_amount, final_price: restPrice });

                    setRefId(record.ref_id);
                    setRefType(record.ref_type);
                    await new Promise((resolve) => setTimeout(resolve, 200));

                    dispatch(setLoading(false));

                    setShowDetail(true);
                  },
                  onError: (error) => {
                    console.log(error);
                    dispatch(setLoading(false));
                  },
                }
              );
            }}
          >
            {_text}
          </Typography.Link>
        );
      },
    },
    {
      // title: 'เวลา',
      // dataIndex: 'order_date',
      render: (_text, record) => {
        return <Typography >
          {_text ? (
            <>
              {calculateTimeDifference(record.order_date, currentTime)}
            </>
          ) : '-'}
        </Typography>
      },
    },
    {
      title: "วันที่สั่งซื้อ",
      dataIndex: "order_date",
      render: (_text) => {
        return (
          <Typography>
            {_text ? formatDateWithTime(_text) : '-'}
          </Typography>
        );
      },
    },
    {
      title: "วันที่ต้องส่งสินค้า",
      dataIndex: "due_date",
      render: (_text) => {
        return (
          <Typography>
            {_text ? formatDate(_text) : '-'}
          </Typography>
        );
      },
    },
    {
      title: "ชื่อผู้ซื้อ",
      dataIndex: "ref_name",
      key: "ref_name",
      render: (_text, record) => {
        return (
          <Typography.Link
            style={{
              cursor: "pointer",
              fontWeight: "400",
              textDecoration: "underline",
              maxWidth: "180px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              display: "inline-block",
            }}
            type="secondary"
            onClick={() => {
              if (record.ref_type === "agent") {
                navigate(`/manage-agent/update-agent/${record.ref_id}?section=order&t=${tabsKey}`);
              } else if (record.ref_type === "retail") {
                navigate(`/manage-agent/update-retail/${record.ref_id}?section=order&t=${tabsKey}`);
              }
            }}
          >
            {_text ?? ''}
            <Typography.Text className="text-order-agent-name">
              {" "}{record && record.ref_alias ? `(${record.ref_alias})` : ''}
            </Typography.Text>
          </Typography.Link>
        );
      },
    },
    /* {
       title: "ประเภทลูกค้า",
       dataIndex: "ref_type",
       key: "ref_type",
       render: (_text) => {
         return <Typography>{_text ?? ''}</Typography>;
       },
     },*/
    {
      title: "เบอร์โทรศัพท์",
      dataIndex: "ref_telephone",
      key: "ref_telephone",
      render: (_text) => {
        return <Typography>{_text}</Typography>;
      },
    },
    /* {
       title: "ประเภทการขนส่ง",
       width: "100px",
       dataIndex: "transport_name",
       render: (_text) => {
         return (
           <Typography>
             {_text}
           </Typography>
         );
       },
     },*/
    {
      title: "ช่องทางการสั่งซื้อ",
      key: "datasource_text",
      width: "100px",
      dataIndex: "datasource_text",
      render: (_text) => {
        return (
          <Typography>
            {_text}
          </Typography>
        );
      },
    },
    {
      title: "ประเภทการชำระเงิน",
      width: "100px",
      dataIndex: "payment_type",
      key: "payment_type",
      render: (_text) => {
        return (
          <Typography style={_text === "COD" ? { color: "#ff4d4f" } : undefined}>
            {getPaymentName(_text, "en")}
          </Typography>
        );
      },
    },
    {
      title: "ราคารวม",
      dataIndex: "result_price",
      render: (_text) => {
        return (
          <Typography>
            {numberFormat(_text)}
          </Typography>
        );
      },
    },
    {
      title: "เครดิตที่ใช้ (COD)",
      dataIndex: "cod_credit_reserved_amount",
      key: "cod_credit_reserved_amount",
      render: (_text, record) => {
        if (record.payment_type !== "COD" || record.cod_credit_reserved_amount == null) {
          return <Typography>-</Typography>;
        }
        const isInsufficient = record.cod_credit_has_debt === true;
        return (
          <Typography style={isInsufficient ? { color: "#ff4d4f" } : undefined}>
            {numberFormat(record.cod_credit_reserved_amount)}
            {isInsufficient && (
              <Tag color="red" style={{ marginLeft: 6 }}>
                เครดิตไม่พอ
              </Tag>
            )}
          </Typography>
        );
      },
    },
    {
      title: "สถานะการชำระเงิน",
      dataIndex: "payment_status",
      render: (_text, record) => {
        // 30/9/2026 — ป้าย "ชำระเงินเกิน" (ม่วง) เหลือเฉพาะออเดอร์เงินโอน (manual_transfer)
        // ซึ่งเป็นเคสเดียวที่โรงงานต้องคืนเงินส่วนเกินให้ลูกค้าเอง · ประเภทอื่น (COD / ตัดบัตร /
        // wallet) เงินส่วนเกินไม่ใช่งานของโรงงาน และออเดอร์ที่สินค้าถูกยกเลิกจนยอดรวมเหลือ 0
        // แต่ลูกค้าจ่ายมาแล้ว เคยขึ้นป้ายม่วงทั้งที่ไม่มีอะไรผิด · เทียบแบบ lower-case เพราะ
        // payment_type บนสายเป็น "COD" ตัวใหญ่แต่ "manual_transfer" ตัวเล็ก (EPaymentType)
        const statusName = getPaymentStatusName(_text);
        const isManualTransfer = record.payment_type?.toLowerCase() === "manual_transfer";
        let restPrice = -((record.total_order_price + record.transport_price) - (record.discount_amount + (record.payment_amount )));
        return (
          <Typography>
            {isManualTransfer && restPrice > 0 ? (
              <Tag color="magenta">ชำระเงินเกิน</Tag>
            ) : (
              <Tag color={statusName?.color}>{statusName?.th}</Tag>
            )}
          </Typography>
        );
      },
    },
    {
      title: "ยอดเงินค้างชำระ",
      key: "rest_price",
      render: (_text, record) => {
        let restPrice = -((record.total_order_price + record.transport_price) - (record.discount_amount + (record.payment_amount  ) ) - (record.pay_point));
        const isManualTransfer = record.payment_type?.toLowerCase() === "manual_transfer";
        return (
          <>
            {/* 30/9/2026 — คู่กับป้ายสถานะ: ยอดที่จ่ายเกินโชว์เฉพาะออเดอร์เงินโอน
                (manual_transfer) ที่โรงงานต้องคืนเงินเอง · ประเภทอื่นขึ้น "-" เหมือนเคสจ่ายพอดี
                เพราะช่องนี้คือ "ยอดค้างชำระ" และออเดอร์ที่จ่ายเกินไม่มียอดค้าง */}
            {restPrice > 0 && isManualTransfer && (
              <Typography>
                {numberFormat(restPrice)}
              </Typography>
            )}
            {(restPrice === 0 || (restPrice > 0 && !isManualTransfer)) && (
              <Typography>
                -
              </Typography>
            )}
            {restPrice < 0 && (
              <Typography style={{ color: 'red' }}>
                {numberFormat(restPrice)}
              </Typography>
            )}
          </>
        );
      },
    },
    {
      title: "จังหวัดปลายทาง",
      dataIndex: "province_name",
      render: (_text) => {
        return (
          <Typography>
            {_text}
          </Typography>
        );
      },
    },
    {
      title: "สถานะการออก Label",
      key: "status_label",
      width: SHIPPING_LABEL_STATUS_COLUMN_WIDTH,
      align: "center",
      render: (_text, record) => (
        <ShippingLabelStatusCell
          record={record}
          queueStatus={queueStatus[record.order_id]}
          mode="prepare_ship"
          onOpenShippingModal={setRetryOrderId}
          selfTracking={getSelfTrackingState(shipItemsByOrderId[record.order_id])}
          onOpenSelfTracking={setSelfTrackingOrderId}
          onOpenOrderDetail={(orderId, paymentStatus, paymentAmount, restPrice) =>
            forceOpenDetail(orderId, false, paymentStatus, paymentAmount, restPrice)
          }
        />
      )
    },
    {
      title: "คืนเงิน",
      key: "refund",
      align: "center",
      fixed: 'right',
      render: (_text, record) => {
        let restPrice = -((record.total_order_price + record.transport_price) - (record.discount_amount + (record.payment_amount )));
        return (
          <div>
            {restPrice > 0 && (
              <Button
                type="default"
                size="small"
                style={{
                  fontSize: '10px'
                }}
                onClick={() => {
                  dispatch(setLoading(true));

                  mutate(
                    {
                      key_type: "order_id",
                      group_by: "factory",
                      items: [record.order_id],
                      factory_id: factory_id,
                    },
                    {
                      onSuccess: async (data) => {
                        setModalData({ ...data.data[0], payment_status: record.payment_status, final_price: restPrice });

                        await new Promise((resolve) => setTimeout(resolve, 200));

                        dispatch(setLoading(false));

                        setShowRefund(true);
                      },
                      onError: (error) => {
                        console.log(error);
                        dispatch(setLoading(false));
                      },
                    }
                  );
                }}
              >
                คืนเงิน
              </Button>
            )}
          </div>)
      }
    },
    // {
    //   title: "จุดจำหน่าย",
    //   key: "retail_pickup_complete",
    //   align: "center",
    //   width: 176,
    //   fixed: "right",
    //   render: (_text, record) => (
    //     <RetailPickupCompleteCell
    //       record={record}
    //       pickupDisabled={isRetailPickupDisabledByShippingQueue(
    //         queueStatus[record.order_id]
    //       )}
    //       onSuccess={() => {
    //         refetchData();
    //         refectShipped();
    //       }}
    //     />
    //   ),
    // },
    // {
    //   title: "เครื่องมือ",
    //   align: "center",
    //   fixed: 'right',
    //   render: (_text, record) => {
    //     return(
    //     <div>
    //       <Button
    //         size="small"
    //         type="default"
    //         onClick={() => {
    //           window.open(`/print-deliverynote/${record?.order_id}`);
    //         }}
    //       >
    //         <PrinterOutlined />
    //       </Button>
    //     </div>)
    //   }
    // },
  ];

  let filtersColumns: (
    | ColumnGroupType<GetDataOrderListItems>
    | ColumnType<GetDataOrderListItems>
  )[] = columns;
  if (IsHiddenPersonInfo) {
    filtersColumns = filtersColumns.filter(
      (column) => column.key !== "ref_name" && column.key !== "ref_telephone"
    );
  }
/* //ให้แสดง คอลัมน์สถานะการออก Label ทุกโรงงาน
  if (!use_platform_transport) {
    filtersColumns = filtersColumns.filter(
      (column) => column.key !== "status_label"
    );
  }
*/
  if (use_platform_transport) {
    filtersColumns = filtersColumns.filter(
    //  (column) => column.key !== "datasource_text" && column.key !== "rest_price" && column.key !== "refund" && column.key !== "payment_type"
      (column) => column.key !== "datasource_text" && column.key !== "rest_price" && column.key !== "refund" 
    );
  }


  return (
    <div>
      {/* ถ้า Order นั้นเป็นแบบสั่งผ่าน Line ให้ disabled checkbox */}
      <Table
        rowSelection={(hasSpeedMerchant || hasThailandPostMerchant) ? rowSelection : undefined}
        rowKey="order_id"
        size="small"
        columns={filtersColumns}
        dataSource={data?.order_data}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: parseInt(data?.total_record as string),
          onChange: (page, pageSize) => {
            dispatch(setDataPrepareShipOffset(pageSize! * (page! - 1)));
            dispatch(setDataPrepareShipLimit(pageSize!));
          },
        }}
        scroll={{ x: true }}
      />

      <ModalOderDetail
        open={showDetail}
        onClose={() => {
          setShowDetail(false);

        }}
        item={modalData as GetOrderDetailData}
        childrenBank={childrenBank}
        refetchData={refetchData}
        showEditModal={showEditModal}
        statusPage="prepare_ship"
        refectShipped={refectShipped}
        refetchCancel={refetchCancel}
      />
      <ModalUpdateOrder
        refId={refId}
        refType={refType}
        open={showEdit}
        item={modalData as GetOrderDetailData}
        setOpen={setShowEdit}
        refetchData={refetchData}
        isProductProblem={isProductProblem}
        forceOpen={forceOpenDetail}
      />
      <ModalRefundPayment
        open={showRefund}
        refund_process={"overpay"}
        onClose={() => {
          setShowRefund(false);
        }}
        item={modalData as GetOrderDetailData}
        refetchData={refetchData}
      />

      <ShippingResultModal
        visible={!!retryOrderId}
        orderId={retryOrderId}
        onClose={() => setRetryOrderId(null)}
        refetchData={() => {
          refetchData();
          // ดึงข้อมูลสถานะคิวทันทีเพื่อให้อัปเดต Badge บนตารางให้เป็นปัจจุบัน
          refreshQueueStatus();
        }}
      />

      <SelfTrackingModal
        visible={!!selfTrackingOrderId}
        orderId={selfTrackingOrderId}
        selfItems={
          selfTrackingOrderId
            ? getSelfTrackingState(shipItemsByOrderId[selfTrackingOrderId])?.pendingItems ?? null
            : []
        }
        onClose={() => setSelfTrackingOrderId(null)}
        onSaved={() => {
          refetchData();
          refectShipped();
          refreshQueueStatus();
        }}
      />
    </div>
  );
});

export default OrderTableList;
